/**
 * Presigned upload DTOs.
 *
 * Drives the mobile upload pipeline (Phase 05 builds the signer, Phase 07
 * wires the route). Two-step flow:
 *
 *   1. Client POSTs PresignedUploadInitRequest with the file metadata.
 *      Server returns PresignedUploadInitResponse with a pre-signed PUT
 *      URL (DO Spaces, S3-compatible) + the storage key it will use.
 *   2. Client PUTs the file bytes directly to that URL.
 *   3. Client POSTs UploadCommitRequest with the key — server verifies the
 *      object exists, attaches it to the target entity (diary photo,
 *      signature, insurance doc, etc.), and returns the durable URL.
 *
 * Allow-list values (purpose + contentType) are enforced server-side to
 * keep arbitrary uploads off the bucket. Extending the list requires a
 * coordinated change to lib/storage/presign.ts (Phase 05).
 */
import { z } from "zod";
import { entityIdSchema } from "../common";

export const UPLOAD_PURPOSES = [
  "diary_photo",
  "incident_photo",
  "plant_inspection_photo",
  "plant_defect_photo",
  "project_image",
  "subcontractor_insurance",
  "subcontractor_rams",
  "subcontractor_liability",
  "subcontractor_hs_policy",
  "worker_certification",
  "signature_image",
  "company_logo",
  "company_insurance",
  "company_liability",
  "company_hs_policy",
  "uploaded_sds",
  "voice_note",
  "source_pdf",
  "portal_credential",
  "rfi_attachment",
  "site_join_request_cscs",
  // Client Permits (#2276) — the photo (or emailed PDF) of a permit the
  // CLIENT issued to us, captured at the gate. Images AND application/pdf,
  // like `source_pdf`: clients increasingly email the permit over, and a
  // photographed printout is strictly worse evidence than the file.
  "client_permit_file",
  // Owner-side Site Access file (drawing / RAMS / CPP / procedure) uploaded by
  // an account OWNER/ADMIN and published to the contractor portal. Deliberately
  // NOT reachable from the contractor portal itself — the portal consumes site
  // files, it never writes them (see `PORTAL_TENANT_UPLOAD_PURPOSES` in
  // `app/api/mobile/v1/_shared.ts`, which this purpose is absent from, so it is
  // gated on the CALLER's own internal account like every other internal
  // purpose).
  "site_file",
] as const;

export type UploadPurpose = (typeof UPLOAD_PURPOSES)[number];

export const UploadPurposeSchema = z.enum(UPLOAD_PURPOSES);

/**
 * Server-enforced content-type allow-list. Anything outside this set
 * returns UPLOAD_BAD_TYPE.
 *
 * Phase 09b: added `audio/m4a`, `audio/mp4`, `audio/wav` for the
 * `voice_note` purpose used by the AI two-step transcribe flow.
 */
export const UPLOAD_CONTENT_TYPES = [
  "image/jpeg",
  "image/png",
  "image/webp",
  "image/heic",
  "application/pdf",
  "audio/m4a",
  "audio/mp4",
  "audio/wav",
] as const;

export type UploadContentType = (typeof UPLOAD_CONTENT_TYPES)[number];

export const UploadContentTypeSchema = z.enum(UPLOAD_CONTENT_TYPES);

/**
 * Default per-purpose ceiling — 20 MB. Most policies in
 * `lib/storage/presign.ts` use this as their `maxBytes`. Purposes whose
 * cap exceeds this (currently only `source_pdf` at 50 MB) declare their
 * own `maxBytes` directly.
 */
export const MAX_UPLOAD_BYTES = 20 * 1024 * 1024;

/**
 * Absolute upper bound for any single upload — the
 * `PresignedUploadInitRequest` schema clamps `sizeBytes` here so the
 * 50 MB `source_pdf` purpose can be accepted at the route boundary.
 * Per-policy `maxBytes` (re-asserted in the signer) is still the real
 * limit each purpose enforces.
 */
export const MAX_UPLOAD_BYTES_HARD = 50 * 1024 * 1024;

export const PresignedUploadInitRequest = z
  .object({
    purpose: UploadPurposeSchema,
    contentType: UploadContentTypeSchema,
    sizeBytes: z.number().int().min(1).max(MAX_UPLOAD_BYTES_HARD),
    filename: z
      .string()
      .min(1)
      .max(255)
      .describe(
        "Original filename. Used for the storage key suffix; sanitised server-side.",
      ),
    /**
     * Optional context fields used by the server to scope the key prefix.
     * For example diary_photo uploads pass `{ projectId }` so the key lands
     * under `users/<userId>/projects/<projectId>/diary/...`.
     */
    projectId: entityIdSchema.optional(),
    documentId: entityIdSchema.optional(),
    subcontractorId: entityIdSchema.optional(),
    workerCertificationId: entityIdSchema.optional(),
    portalCredentialId: entityIdSchema.optional(),
    rfiId: entityIdSchema.optional(),
    siteJoinRequestId: entityIdSchema.optional(),
  })
  .strict();

export type PresignedUploadInitRequest = z.infer<
  typeof PresignedUploadInitRequest
>;

// ───────────────────────────────────────────────────────────────────────────
// Capture-time upload policy mirror (#2345)
// ───────────────────────────────────────────────────────────────────────────

/**
 * Purposes whose files the mobile capture flows validate LOCALLY before the
 * capture is queued for an offline replay — every purpose used by
 * `captureUpload` (project photos, contractor credentials, site files) and by
 * `capturePlantEvidence` (inspection / defect photos). A purpose not listed
 * here has no mirrored policy, so a capture flow cannot promise the server
 * will accept it: adding a purpose to a capture flow means adding its mirror
 * here (and to the pin test) in the same change.
 */
export const UPLOAD_CAPTURE_PURPOSES = [
  "project_image",
  "portal_credential",
  "site_file",
  "plant_inspection_photo",
  "plant_defect_photo",
] as const;

export type UploadCapturePurpose = (typeof UPLOAD_CAPTURE_PURPOSES)[number];

/**
 * Per-purpose file limits, mirrored from the server signer
 * (`lib/storage/presign.ts` `POLICIES`) so a client can refuse an over-limit
 * or wrong-type capture BEFORE it is queued for an offline replay — exactly
 * the `CLIENT_PERMIT_FILE_*` pattern from #2276, generalised to the other
 * capture families. A queued capture the server will permanently 4xx is worse
 * than an immediate error: the sync UI can only retry the same invalid
 * payload, long after the paper evidence has left the worker's hand. The
 * contracts package may not import from the Next.js app, so each entry is
 * pinned equal to the signer's policy by `__tests__/lib/storage/presign.test.ts`.
 */
export interface UploadCaptureFileLimits {
  /** Hard cap in bytes; re-asserted by the signer at sign and commit time. */
  maxBytes: number;
  /** Allow-list of content types accepted for this purpose. */
  contentTypes: readonly UploadContentType[];
}

const CAPTURE_IMAGE_TYPES = [
  "image/jpeg",
  "image/png",
  "image/heic",
  "image/webp",
] as const;
const CAPTURE_PDF_TYPES = ["application/pdf"] as const;

export const UPLOAD_CAPTURE_FILE_LIMITS: Record<
  UploadCapturePurpose,
  UploadCaptureFileLimits
> = {
  project_image: {
    contentTypes: CAPTURE_IMAGE_TYPES,
    maxBytes: Math.min(25 * 1024 * 1024, MAX_UPLOAD_BYTES),
  },
  portal_credential: {
    contentTypes: [...CAPTURE_IMAGE_TYPES, ...CAPTURE_PDF_TYPES],
    maxBytes: MAX_UPLOAD_BYTES,
  },
  site_file: {
    // Matches the web multipart route's 25 MB cap exactly (see the signer's
    // own comment) rather than the 20 MB shared default.
    contentTypes: [...CAPTURE_IMAGE_TYPES, ...CAPTURE_PDF_TYPES],
    maxBytes: 25 * 1024 * 1024,
  },
  plant_inspection_photo: {
    contentTypes: CAPTURE_IMAGE_TYPES,
    maxBytes: Math.min(25 * 1024 * 1024, MAX_UPLOAD_BYTES),
  },
  plant_defect_photo: {
    contentTypes: CAPTURE_IMAGE_TYPES,
    maxBytes: Math.min(25 * 1024 * 1024, MAX_UPLOAD_BYTES),
  },
};

/** Human "20 MB" for capture-time policy errors. Never shows raw bytes. */
export function formatUploadBytes(bytes: number): string {
  const mb = bytes / (1024 * 1024);
  return `${mb >= 10 ? Math.round(mb) : mb.toFixed(0)} MB`;
}

/**
 * One picked file as the capture flow holds it, checked against the SAME
 * per-purpose policy the signer enforces (`UploadCaptureFileLimits`) plus the
 * filename/size rules of `PresignedUploadInitRequest`. Validating this at
 * CAPTURE time is what turns "queued, then permanently rejected hours later"
 * into "fix it while the file is still in the picker".
 *
 * Deliberately NOT `.strict()` (unlike `ClientPermitCaptureFileSchema`): the
 * capture families attach extra per-family fields (title, includeInCpp,
 * itemIndex, …) to the same object, and this schema's job is policy
 * enforcement — not shape enforcement, which TypeScript owns.
 */
export const UploadCaptureFileSchema = z
  .object({
    purpose: z.enum(UPLOAD_CAPTURE_PURPOSES),
    contentType: UploadContentTypeSchema,
    sizeBytes: z.number().int().positive(),
    filename: z.string().min(1).max(255),
  })
  .superRefine((file, ctx) => {
    const limits = UPLOAD_CAPTURE_FILE_LIMITS[file.purpose];
    if (!limits.contentTypes.includes(file.contentType)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["contentType"],
        message: "This file type can't be uploaded here",
      });
    }
    if (file.sizeBytes > limits.maxBytes) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["sizeBytes"],
        message: `Files must be ${formatUploadBytes(limits.maxBytes)} or smaller`,
      });
    }
  });

/**
 * RESPONSE schema — deliberately NOT `.strict()` (issue #2411). A mobile
 * build parses this with the schema it was compiled against, so a server that
 * adds a field would make an installed build FAIL to parse rather than ignore
 * the new key. Required upload controls remain fail-closed in the client via
 * `parseRequiredWireResponse`. Enforced by the mobile boundary tests and
 * `__tests__/contracts/mobile-wire-strictness.test.ts`.
 */
export const PresignedUploadInitResponse = z.object({
  uploadUrl: z
    .string()
    .describe(
      "Pre-signed PUT URL. Client uploads bytes here with the same contentType it declared on init.",
    ),
  key: z
    .string()
    .describe(
      "Opaque storage key. Pass back on commit. Do NOT construct keys client-side.",
    ),
  expiresAt: z
    .string()
    .datetime()
    .describe(
      "UTC timestamp after which the upload URL stops working (TTL <= 5 min).",
    ),
  requiredHeaders: z
    .record(z.string(), z.string())
    .describe(
      "Headers the client MUST set on the PUT (e.g. content-type, x-amz-meta-*).",
    ),
});

export type PresignedUploadInitResponse = z.infer<
  typeof PresignedUploadInitResponse
>;

export const UploadCommitRequest = z
  .object({
    key: z.string().min(1),
    purpose: UploadPurposeSchema,
    projectId: entityIdSchema.optional(),
    documentId: entityIdSchema.optional(),
    subcontractorId: entityIdSchema.optional(),
    workerCertificationId: entityIdSchema.optional(),
    portalCredentialId: entityIdSchema.optional(),
    rfiId: entityIdSchema.optional(),
    siteJoinRequestId: entityIdSchema.optional(),
  })
  .strict();

export type UploadCommitRequest = z.infer<typeof UploadCommitRequest>;

/**
 * REQUEST for POST /api/mobile/v1/uploads/abandon — the ABANDON half of the
 * committed-upload settlement (#2359). The offline outbox's senders
 * checkpoint a committed transfer on-device and resume at the ATTACH step;
 * when an entry is terminally failed or discarded WITHOUT its attach ever
 * landing (a definitive server refusal, un-persistable checkpoint progress),
 * the sender reports the checkpoint GONE so the server can release the
 * committed-checkpoint hold that has been shielding the object from the
 * storage GC.
 *
 * `key` accepts the bare storage key OR the durable URL returned by commit
 * (the sender checkpoints the URL for some families); the server normalises
 * both to the key. Abandon is IDEMPOTENT and total: a key with no hold (or
 * no candidate at all — the attach already cleared it) is a successful
 * no-op, because the caller fires this best-effort while discarding an
 * outbox entry and must never block on the answer.
 */
export const UploadAbandonRequest = z
  .object({
    key: z
      .string()
      .min(1)
      .describe(
        "Storage key (or durable URL) of the committed upload whose device checkpoint is gone.",
      ),
  })
  .strict();

export type UploadAbandonRequest = z.infer<typeof UploadAbandonRequest>;

/** RESPONSE schema — deliberately NOT `.strict()`; see the note on
 * `PresignedUploadInitResponse` above and issue #2411. */
export const UploadAbandonResponse = z.object({
  released: z
    .boolean()
    .describe(
      "True when a committed-checkpoint hold was actually cleared. False is a successful no-op (no hold, no candidate — the attach or an earlier abandon already settled it).",
    ),
});

export type UploadAbandonResponse = z.infer<typeof UploadAbandonResponse>;

/** RESPONSE schema — deliberately NOT `.strict()`; see the note on
 * `PresignedUploadInitResponse` above and issue #2411. */
export const UploadCommitResponse = z.object({
  url: z
    .string()
    .describe(
      "Durable URL to read the asset. CDN-fronted for public assets, presigned-read for private.",
    ),
  key: z.string(),
  bytes: z.number().int().min(0),
  contentType: UploadContentTypeSchema,
});

export type UploadCommitResponse = z.infer<typeof UploadCommitResponse>;
